import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPrice } from "../../lib/api";
import { useSeo } from "../../lib/useSeo";
import { sourceLabel } from "../../components/SourceBadge";

const Card = ({ d }) => (
  <Link
    to={`/huurwoning/${d.slug}`}
    className="block overflow-hidden rounded-2xl border border-slate-200 transition-shadow hover:shadow-lg"
    data-testid="seo-similar-card"
  >
    {d.image ? (
      <img src={d.image} alt={`${(d.type || "woning")} te huur in ${d.city}`} loading="lazy" className="h-40 w-full object-cover" />
    ) : (
      <div className="h-40 w-full bg-slate-100" />
    )}
    <div className="p-3">
      <div className="text-sm font-bold text-slate-900">
        {(d.type || "Woning")[0].toUpperCase() + (d.type || "woning").slice(1)} · {formatPrice(d.price)} p/m
      </div>
      <div className="text-xs text-slate-500">
        {d.m2} m² · {d.bedrooms} slaapkamers · {d.city}
      </div>
    </div>
  </Link>
);

const ListingSeo = () => {
  const { stad, slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api(`/seo/listing/${stad}/${slug}`)
      .then((d) => (d.found ? setData(d) : setNotFound(true)))
      .catch(() => setNotFound(true));
  }, [stad, slug]);

  useSeo(
    data
      ? {
          title: data.page_title,
          exactTitle: true,
          description: data.meta_description,
          image: data.og_image,
          robots: data.robots,
          jsonLd: [data.jsonld, data.breadcrumb_jsonld].filter(Boolean),
        }
      : { title: "Woning", robots: "noindex,follow" }
  );

  if (notFound) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-2xl font-bold text-slate-900">Woning niet gevonden</h1>
        <p className="mt-2 text-slate-500">Deze advertentie bestaat niet (meer).</p>
        <Link to="/woningen" className="mt-6 inline-block rounded-xl bg-brandblue px-6 py-3 font-semibold text-white">
          Bekijk alle woningen
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <div className="aspect-[16/9] w-full animate-pulse rounded-3xl bg-slate-100" />
        <div className="mt-6 h-8 w-1/2 animate-pulse rounded bg-slate-100" />
      </div>
    );
  }

  const l = data.listing;
  const facts = [
    ["Type", l.type_label],
    ["Oppervlakte", `${l.m2} m²`],
    ["Slaapkamers", l.bedrooms],
    ["Energielabel", l.energy_label || "—"],
    ["Beschikbaar", l.available_from || "—"],
    ...(l.income_required ? [["Inkomenseis", formatPrice(l.income_required)]] : []),
    ["Aanbieder", sourceLabel(l.source)],
  ];

  return (
    <div className="mx-auto max-w-5xl px-4 py-6" data-testid="seo-listing-page">
      <nav className="mb-3 text-sm text-slate-400" data-testid="seo-breadcrumbs">
        <Link to={`/huurwoningen/${l.city_slug}`} className="hover:text-slate-600">Huurwoningen</Link>
        {" › "}
        <Link to={`/huurwoningen/${l.city_slug}`} className="hover:text-slate-600">{l.city}</Link>
        {" › "}
        <Link to={`/${data.type_cat_path}/${l.city_slug}`} className="hover:text-slate-600">{l.type_label}</Link>
        {" › "}
        <span>{l.street}</span>
      </nav>

      {!data.is_active && (
        <div className="mb-4 rounded-2xl border border-orange-200 bg-orange-50 p-4 font-semibold text-orange-800" data-testid="seo-expired-notice">
          Deze woning is niet meer beschikbaar. Bekijk hieronder vergelijkbare beschikbare woningen in {l.city}.
        </div>
      )}

      <h1 className="font-display text-3xl font-extrabold text-slate-900" data-testid="seo-h1">{data.h1}</h1>
      <p className="mt-1 text-slate-500">
        {l.street}{l.neighborhood ? `, ${l.neighborhood}` : ""}, {l.city}
      </p>
      <div className="mt-2 text-2xl font-extrabold text-slate-900" data-testid="seo-price">
        {formatPrice(l.price)} <span className="text-sm font-semibold text-slate-400">p/m</span>
      </div>

      {l.gallery && l.gallery.length > 0 && (
        <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
          {l.gallery.slice(0, 4).map((img, i) => (
            <img
              key={i}
              src={img}
              alt={`${data.h1} – foto ${i + 1}`}
              loading={i === 0 ? "eager" : "lazy"}
              className={`w-full rounded-xl object-cover ${i === 0 ? "col-span-2 row-span-2 h-64" : "h-32"}`}
            />
          ))}
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        {facts.map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-slate-200 p-4">
            <div className="text-xs text-slate-400">{k}</div>
            <div className="font-display text-base font-bold text-slate-900">{v}</div>
          </div>
        ))}
      </div>

      {l.description && (
        <>
          <h2 className="mt-8 font-display text-xl font-bold text-slate-900">Beschrijving</h2>
          <p className="mt-3 whitespace-pre-line leading-relaxed text-slate-600">{l.description}</p>
        </>
      )}

      <div className="mt-8 rounded-3xl bg-navy p-7 text-white">
        <h2 className="font-display text-xl font-bold">Bekijk jouw persoonlijke Match Score</h2>
        <p className="mt-2 text-slate-300">
          Log in of maak een gratis account aan om te zien hoe goed deze woning bij jouw profiel past — en krijg direct een melding zodra een vergelijkbare woning online komt.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link to={`/woning/${l.listing_id}`} className="rounded-full bg-brandblue px-6 py-3 font-bold text-white" data-testid="seo-cta-app">
            Open in WoningAlert
          </Link>
          <Link to="/register" className="rounded-full border border-white/60 px-6 py-3 font-bold text-white" data-testid="seo-cta-register">
            Maak gratis een woningalert aan
          </Link>
        </div>
      </div>

      {data.similar && data.similar.length > 0 && (
        <>
          <h2 className="mt-8 font-display text-xl font-bold text-slate-900">Vergelijkbare woningen in {l.city}</h2>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {data.similar.map((d) => <Card key={d.slug} d={d} />)}
          </div>
        </>
      )}

      <h2 className="mt-8 font-display text-xl font-bold text-slate-900">Meer huurwoningen ontdekken</h2>
      <div className="mt-3 flex flex-wrap gap-2" data-testid="seo-internal-links">
        <Link to={`/huurwoningen/${l.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Alle huurwoningen in {l.city}</Link>
        <Link to={`/${data.type_cat_path}/${l.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Alle {l.type}s in {l.city}</Link>
        <Link to={`/huurwoningen-onder-1500/${l.city_slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Vergelijkbare woningen onder €1.500</Link>
        {data.nearby.map((nb) => (
          <Link key={nb.slug} to={`/huurwoningen/${nb.slug}`} className="rounded-full border border-slate-200 px-4 py-2 text-sm text-slate-700">Huurwoningen in {nb.name}</Link>
        ))}
      </div>
    </div>
  );
};

export default ListingSeo;
